export const formatFull = (
    date: Date,
    includeSecondsAndNonZeroMs?: boolean
): string => {
    const format: Intl.DateTimeFormatOptions = {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "numeric"
    };

    if (includeSecondsAndNonZeroMs) {
        format.second = "numeric";
    }

    const parts = ensureDate(date)
        .toLocaleDateString("en-gb", format)
        .split(",");

    return (
        (parts[0] ?? "") +
        " " +
        addLeadingZero(parts[1]?.trim() ?? "") +
        (parts[2] ?? "") +
        getNonZeroMilliseconds(date, includeSecondsAndNonZeroMs)
    );
};

export const formatShort = (
    date: Date,
    includeSecondsAndNonZeroMs?: boolean
): string => {
    const format: Intl.DateTimeFormatOptions = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "numeric"
    };
    if (includeSecondsAndNonZeroMs) {
        format.second = "numeric";
    }
    const parts = ensureDate(date)
        .toLocaleDateString("en-gb", format)
        .split(",");

    return (
        addLeadingZero(parts[0] ?? "") +
        (parts[1] ?? "") +
        getNonZeroMilliseconds(date, includeSecondsAndNonZeroMs)
    );
};

export const formatShortDateOnly = (date: Date): string => {
    return addLeadingZero(
        ensureDate(date).toLocaleDateString("en-gb", {
            year: "numeric",
            month: "short",
            day: "numeric"
        })
    );
};

export const formatShortTimeOnly = (
    date: Date,
    includeSecondsAndNonZeroMs?: boolean
): string => {
    const format: Intl.DateTimeFormatOptions = {
        hour: "numeric",
        minute: "numeric"
    };
    if (includeSecondsAndNonZeroMs) {
        format.second = "numeric";
    }
    return (
        addLeadingZero(ensureDate(date).toLocaleTimeString("en-gb", format)) +
        getNonZeroMilliseconds(date, includeSecondsAndNonZeroMs)
    );
};

export const formatInterval = (
    start: Date,
    end: Date,
    includeSecondsAndNonZeroMs?: boolean
): string => {
    const startDate = ensureDate(start);
    const endDate = ensureDate(end);

    if (
        startDate.getFullYear() === endDate.getFullYear() &&
        startDate.getMonth() === endDate.getMonth() &&
        startDate.getDate() === endDate.getDate()
    ) {
        return `${formatFull(
            startDate,
            includeSecondsAndNonZeroMs
        )} - ${formatShortTimeOnly(endDate, includeSecondsAndNonZeroMs)}`;
    }

    return `${formatFull(startDate, includeSecondsAndNonZeroMs)} - ${formatFull(
        endDate,
        includeSecondsAndNonZeroMs
    )}`;
};

export function ensureDate(date: Date): Date {
    // contracts in the main reddeer repo specify the type Date, but the value is actually a string
    // this code protects against this common issue
    if (typeof date === "string") {
        return new Date(date);
    }
    return date;
}

export function asUtc(date: Date) {
    const utc = Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        date.getSeconds(),
        date.getMilliseconds()
    );
    return new Date(utc);
}

function addLeadingZero(text: string): string {
    const bits = text.split(" ");
    const char = bits[0]?.trim().charAt(0);
    if (
        bits[0]?.trim().length === 1 &&
        [1, 2, 3, 4, 5, 6, 7, 8, 9].find(x => x.toString() === char)
    ) {
        return "0" + text;
    }
    return text;
}

function getNonZeroMilliseconds(
    date: Date,
    includeSecondsAndNonZeroMs?: boolean
) {
    if (includeSecondsAndNonZeroMs) {
        const dateObj = new Date(date);
        const ms = dateObj.getMilliseconds();
        if (ms > 0) {
            return ":" + ms.toString().padStart(3, "0");
        }
    }
    return "";
}
